import axios from "axios";
import { getToken } from "firebase/app-check";
import { API_URL } from "./functions-origin";
import { appCheckInstance } from "./firebase-config";

const apiUrl = API_URL;

// Every call in this file goes through the default axios instance, so a single request
// interceptor here covers all of them rather than needing to thread the header through each
// function individually. getToken() returns a cached token (refreshed automatically in the
// background per isTokenAutoRefreshEnabled in firebase-config.js) rather than always minting a
// new one, so this doesn't add a network round trip to every call. Scoped to just this app's own
// API (apiUrl) so the token is never sent to some other origin a future axios call might target.
// Failure to get a token (e.g. reCAPTCHA/network hiccup) is swallowed rather than blocking the
// request - the backend will correctly reject a request with no App Check header rather than this
// leaving every API call hanging.
axios.interceptors.request.use(async (config) => {
  if (typeof config.url === "string" && config.url.startsWith(apiUrl)) {
    try {
      const { token } = await getToken(appCheckInstance);
      config.headers = config.headers ?? {};
      config.headers["X-Firebase-AppCheck"] = token;
    } catch (err) {
      console.error("Failed to attach App Check token", err);
    }
  }
  return config;
});

export async function logIn({ email, password }) {
  const url = `${apiUrl}/login`;
  const res = await axios.post(url, { email, password });
  return res.data;
}

// Public - no auth token, since this is called from the login screen before anyone's signed in.
// Only ever returns a company's logo/brand colour/name, nothing sensitive.
export async function getCompanyBranding({ email }) {
  const url = `${apiUrl}/company-branding`;
  const res = await axios.get(url, { params: { email } });
  return res.data;
}

// Used by T&C Verification to translate a document to English before re-checking it. Signed-in
// only, not company-scoped - this is a stateless text-processing utility, not company data.
export async function translateText({ userIdToken, text }) {
  const url = `${apiUrl}/translate`;
  const res = await axios.post(
    url,
    { text },
    {
      headers: {
        Authorization: `Bearer ${userIdToken}`,
      },
    }
  );
  return res.data;
}

// USERS

export async function getUser({ userIdToken, uid }) {
  const url = `${apiUrl}/user/${uid}`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

export async function updateUser({
  userIdToken,
  companyId,
  uid,
  name,
  email,
  access,
  autoProjects,
  testrail,
  jira_boards,
  extra_roles,
  verified,
  away_name,
}) {
  const url = `${apiUrl}/company/${companyId}/users/${uid}`;
  const res = await axios.put(
    url,
    { email, name, access, autoProjects, testrail, jira_boards, extra_roles, verified, away_name },
    {
      headers: {
        Authorization: `Bearer ${userIdToken}`,
      },
    }
  );
  return res.data;
}

export async function verifyEmail({ code }) {
  const url = `${apiUrl}/verifyEmail/${code}`;
  const res = await axios.post(url);
  return res.data;
}

export async function resetPassword({ code, password }) {
  const url = `${apiUrl}/verifyEmail/${code}`;
  const res = await axios.post(url, { password });
  return res.data;
}

// Employees

export async function getEmployees({ userIdToken, companyId }) {
  const url = `${apiUrl}/company/${companyId}/employees`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

export async function createUser({
  userIdToken,
  companyId,
  name,
  email,
  password,
  role,
  sendVerification,
  automationProjects,
}) {
  const url = `${apiUrl}/company/${companyId}/users`;
  const res = await axios.post(
    url,
    { name, email, password, role, sendVerification, automationProjects },
    {
      headers: {
        Authorization: `Bearer ${userIdToken}`,
      },
    }
  );
  return res.data;
}

export async function deleteUser({ userIdToken, companyId, uid }) {
  const url = `${apiUrl}/company/${companyId}/users/${uid}`;
  const res = await axios.delete(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

// Clients

export async function getClients({ userIdToken, companyId }) {
  const url = `${apiUrl}/company/${companyId}/clients`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

// AZURE

export async function getAutomationProjects({ userIdToken, companyId }) {
  const url = `${apiUrl}/company/${companyId}/azure/projects`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

export async function getAutomationProject({ userIdToken, companyId, projectId }) {
  const url = `${apiUrl}/company/${companyId}/azure/projects/${projectId}`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

export async function getAzureOrganisations({ userIdToken, companyId }) {
  const url = `${apiUrl}/company/${companyId}/azure/organisations`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

// BUILDS

export async function getAutomationBuilds({ userIdToken, companyId, projectId }) {
  const url = `${apiUrl}/company/${companyId}/azure/${projectId}/builds`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}
export async function getAutomationBuild({ userIdToken, companyId, projectId, buildId }) {
  const url = `${apiUrl}/company/${companyId}/azure/${projectId}/builds/${buildId}`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}
export async function getAutomationBuildLogs({ userIdToken, companyId, projectId, buildId }) {
  const url = `${apiUrl}/company/${companyId}/azure/${projectId}/builds/${buildId}/logs`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}
export async function getAutomationBuildLog({ userIdToken, companyId, projectId, buildId, logId }) {
  const url = `${apiUrl}/company/${companyId}/azure/${projectId}/builds/${buildId}/logs/${logId}`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

// PIPELINES

export async function getAutomationPipelines({ userIdToken, companyId, projectId }) {
  const url = `${apiUrl}/company/${companyId}/azure/${projectId}/pipelines`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}
export async function getAutomationPipelineRuns({ userIdToken, companyId, projectId, pipelineId }) {
  const url = `${apiUrl}/company/${companyId}/azure/${projectId}/pipelines/${pipelineId}/runs`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

// SMS
export async function getSMSHistory({ userIdToken, companyId, simId }) {
  const url = `${apiUrl}/company/${companyId}/sms/${simId}`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

// BOOKER
export async function getAwayEvents({ userIdToken, companyId }) {
  const url = `${apiUrl}/company/${companyId}/booker/away`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

// RELEASES

// Apple
export async function getAppleReleases({ os, showBeta, showRC }) {
  var url = `${apiUrl}/releases/apple/${os}`;

  if (showBeta) {
    url = `${apiUrl}/releases/apple/${os}/beta`;
  }

  if (showRC) {
    url = `${apiUrl}/releases/apple/${os}/rc`;
  }

  const res = await axios.get(url);
  return res.data;
}

// Google/Android
export async function getGoogleReleases() {
  const res = await axios.get(`${apiUrl}/releases/google`);
  return res.data;
}

// TESTRAIL
// Get all statuses
export async function getTestrailStatuses({ userIdToken, companyId }) {
  const url = `${apiUrl}/company/${companyId}/testrail/statuses`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}
// Get all projects
export async function getTestrailProjects({ userIdToken, companyId }) {
  const url = `${apiUrl}/company/${companyId}/testrail/projects`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}
// Get single project
export async function getTestrailProject({ userIdToken, companyId, projectId }) {
  const url = `${apiUrl}/company/${companyId}/testrail/projects/${projectId}`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}
// Get All runs for a project
export async function getTestrailProjectRuns({ userIdToken, companyId, projectId }) {
  const url = `${apiUrl}/company/${companyId}/testrail/runs/${projectId}`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}
// Get all tests for a run
export async function getTestrailProjectRunTests({ userIdToken, companyId, runId }) {
  const url = `${apiUrl}/company/${companyId}/testrail/tests/${runId}`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}
// Get sesctions for tests
export async function getTestrailProjectRunTestSections({ userIdToken, companyId, projectId, suiteId }) {
  const url = `${apiUrl}/company/${companyId}/testrail/projects/${projectId}/suite/${suiteId}`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

// Jira Boards
//  All boards
export async function getJiraBoards({ userIdToken, companyId }) {
  const url = `${apiUrl}/company/${companyId}/jira/boards`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}
// Board config
export async function getJiraBoardConfig({ userIdToken, companyId, boardId }) {
  const url = `${apiUrl}/company/${companyId}/jira/boards/${boardId}`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}
// Board issues
export async function getJiraBoardIssues({ userIdToken, companyId, boardId }) {
  const url = `${apiUrl}/company/${companyId}/jira/boards/${boardId}/issues`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

// HARVEST
export async function getHarvestTimeEntries({ userIdToken, companyId, from, to }) {
  const url = `${apiUrl}/company/${companyId}/harvest/time-entries?from=${from}&to=${to}`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

export async function postHarvestTimeEntry({ userIdToken, companyId, projectId, taskId, spentDate, hours, notes }) {
  const url = `${apiUrl}/company/${companyId}/harvest/time-entries`;
  const res = await axios.post(
    url,
    { project_id: projectId, task_id: taskId, spent_date: spentDate, hours, notes },
    {
      headers: {
        Authorization: `Bearer ${userIdToken}`,
      },
    },
  );
  return res.data;
}

export async function patchHarvestTimeEntry({ userIdToken, companyId, timeEntryId, projectId, taskId, hours, notes }) {
  const url = `${apiUrl}/company/${companyId}/harvest/time-entries/${timeEntryId}`;
  const res = await axios.patch(
    url,
    { project_id: projectId, task_id: taskId, hours, notes },
    {
      headers: {
        Authorization: `Bearer ${userIdToken}`,
      },
    },
  );
  return res.data;
}

export async function deleteHarvestTimeEntry({ userIdToken, companyId, timeEntryId }) {
  const url = `${apiUrl}/company/${companyId}/harvest/time-entries/${timeEntryId}`;
  const res = await axios.delete(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

export async function getHarvestProjectAssignments({ userIdToken, companyId }) {
  const url = `${apiUrl}/company/${companyId}/harvest/project-assignments`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

// FORECAST
export async function getForecastSchedule({ userIdToken, companyId, startDate, endDate }) {
  const url = `${apiUrl}/company/${companyId}/forecast/schedule?startDate=${startDate}&endDate=${endDate}`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

// HARVEST/FORECAST OAUTH
export async function getHarvestOAuthAuthorizeUrl({ userIdToken, companyId, state }) {
  const url = `${apiUrl}/company/${companyId}/harvest/oauth/authorize-url?state=${encodeURIComponent(state)}`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

export async function postHarvestOAuthCallback({ userIdToken, companyId, code }) {
  const url = `${apiUrl}/company/${companyId}/harvest/oauth/callback`;
  const res = await axios.post(
    url,
    { code },
    {
      headers: {
        Authorization: `Bearer ${userIdToken}`,
      },
    },
  );
  return res.data;
}

export async function getHarvestOAuthStatus({ userIdToken, companyId }) {
  const url = `${apiUrl}/company/${companyId}/harvest/oauth/status`;
  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

export async function deleteHarvestOAuthConnection({ userIdToken, companyId }) {
  const url = `${apiUrl}/company/${companyId}/harvest/oauth/connection`;
  const res = await axios.delete(url, {
    headers: {
      Authorization: `Bearer ${userIdToken}`,
    },
  });
  return res.data;
}

// NOTIFICATIONS
// Subscribes/unsubscribes this device's FCM token to the company's post-notification topic(s) -
// see functions/express/routes/notifications/subscribe-topics.js. Which topic(s) is decided
// server-side off this person's own verified ID token claims, not anything sent up here.
export async function subscribeToPostNotifications({ userIdToken, companyId, token }) {
  const url = `${apiUrl}/company/${companyId}/notifications/subscribe`;
  const res = await axios.post(
    url,
    { token },
    {
      headers: {
        Authorization: `Bearer ${userIdToken}`,
      },
    }
  );
  return res.data;
}

export async function unsubscribeFromPostNotifications({ userIdToken, companyId, token }) {
  const url = `${apiUrl}/company/${companyId}/notifications/unsubscribe`;
  const res = await axios.post(
    url,
    { token },
    {
      headers: {
        Authorization: `Bearer ${userIdToken}`,
      },
    }
  );
  return res.data;
}

// Called right after addPost/updatePost (see src/services/Firestore.js) - asks the backend to
// check whether this specific post just went live and, if so, send the notification. There's no
// Firestore-triggered Cloud Function doing this automatically (see functions/index.js's own
// comment on notifyScheduledPosts for why), so this call is what actually catches an immediate
// publish - the backend's own scheduled sweep still independently covers a *scheduled* post's
// publish_date elapsing later with nobody around to make this call.
export async function notifyPostPublishedCheck({ userIdToken, companyId, postId }) {
  const url = `${apiUrl}/company/${companyId}/notifications/notify-post-published`;
  const res = await axios.post(
    url,
    { postId },
    {
      headers: {
        Authorization: `Bearer ${userIdToken}`,
      },
    }
  );
  return res.data;
}

export async function postSendEmail({
  userIdToken,
  companyId,
  templateId,
  toEmails,
  holidayStart,
  holidayEnd,
  fromName,
  declineReason,
}) {
  const url = `${apiUrl}/company/${companyId}/send/email/${templateId}`;
  const res = await axios.post(
    url,
    { toEmails, holidayStart, holidayEnd, fromName, declineReason },
    {
      headers: {
        Authorization: `Bearer ${userIdToken}`,
      },
    }
  );
  return res.data;
}
export async function postSendEmailRaw({
  userIdToken,
  templateId,
  toEmails,
  ccEmails,
  holidayStart,
  holidayEnd,
  fromName,
  declineReason,
  fromEmail,
  apiKey,
}) {
  const url = `${apiUrl}/send/email/${templateId}`;
  const res = await axios.post(
    url,
    { toEmails, ccEmails, holidayStart, holidayEnd, fromName, declineReason, fromEmail, apiKey },
    {
      headers: {
        Authorization: `Bearer ${userIdToken}`,
      },
    }
  );
  return res.data;
}

// RUNWAY
// Project/version/build metadata is mostly read/written directly via Firestore (see
// src/services/Runway.js) - these are only the operations that need server-side logic: cascading
// deletes, the share password, signed download URLs, and the client-facing assignments lookup.
// See functions/express/routes/runway/* for the matching backend implementations.

export async function deleteRunwayProject({ userIdToken, companyId, projectId }) {
  const url = `${apiUrl}/company/${companyId}/runway/projects/${projectId}`;
  const res = await axios.delete(url, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
  return res.data;
}

export async function deleteRunwayVersion({ userIdToken, companyId, projectId, versionId }) {
  const url = `${apiUrl}/company/${companyId}/runway/projects/${projectId}/versions/${versionId}`;
  const res = await axios.delete(url, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
  return res.data;
}

export async function deleteRunwayBuild({ userIdToken, companyId, projectId, versionId, buildId }) {
  const url = `${apiUrl}/company/${companyId}/runway/projects/${projectId}/versions/${versionId}/builds/${buildId}`;
  const res = await axios.delete(url, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
  return res.data;
}

export async function setRunwayPassword({ userIdToken, companyId, projectId, password }) {
  const url = `${apiUrl}/company/${companyId}/runway/projects/${projectId}/password`;
  const res = await axios.post(
    url,
    { password },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
  return res.data;
}

// Used by both the management UI (previewing/testing an upload) and, implicitly, an assigned
// client via MyBuilds.js - the backend route itself decides which of those two the caller
// actually is and checks their assignment accordingly; this call looks identical either way.
export async function getRunwayDownloadUrl({ userIdToken, companyId, projectId, versionId, buildId }) {
  const url = `${apiUrl}/company/${companyId}/runway/projects/${projectId}/versions/${versionId}/builds/${buildId}/download`;
  const res = await axios.get(url, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
  return res.data;
}

export async function getMyRunwayAssignments({ userIdToken, companyId }) {
  const url = `${apiUrl}/company/${companyId}/runway/my-assignments`;
  const res = await axios.get(url, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
  return res.data;
}

// Public share link (src/pages/runway/RunwayShare.js) - deliberately no Authorization header on
// any of these three, they're reachable by anyone with the link, gated by the project's own
// password instead of a Portal session.

// No password required - just enough (project name/icon/colour) for the share page to brand
// itself the moment it loads, before anyone's entered anything. See getRunwaySharePreview in
// functions/express/routes/runway/share.js.
export async function getRunwaySharePreview({ companyId, shareToken }) {
  const url = `${apiUrl}/company/${companyId}/runway/share/${shareToken}/preview`;
  const res = await axios.get(url);
  return res.data;
}

export async function unlockRunwayShare({ companyId, shareToken, password }) {
  const url = `${apiUrl}/company/${companyId}/runway/share/${shareToken}/unlock`;
  const res = await axios.post(url, { password });
  return res.data;
}

export async function getRunwayShareDetails({ companyId, shareToken, accessToken }) {
  const url = `${apiUrl}/company/${companyId}/runway/share/${shareToken}/details`;
  const res = await axios.get(url, { params: { access_token: accessToken } });
  return res.data;
}

// buildId is always required; versionId is only needed (and only trusted server-side) when the
// share link itself is a project link spanning multiple versions - a version link already knows
// exactly which version it's rooted at. See getRunwayShareDownload in
// functions/express/routes/runway/share.js.
export async function getRunwayShareDownload({ companyId, shareToken, accessToken, versionId, buildId }) {
  const url = `${apiUrl}/company/${companyId}/runway/share/${shareToken}/download`;
  const res = await axios.get(url, { params: { access_token: accessToken, version_id: versionId, build_id: buildId } });
  return res.data;
}

// Not an axios call - just builds the URL for an iOS OTA install manifest (see
// getRunwayShareManifest in functions/express/routes/runway/share.js), which RunwayShare.js needs
// as a plain string to hand to an itms-services://?action=download-manifest link. iOS Safari
// fetches this URL itself once the link is tapped, so there's nothing to await here.
export function getRunwayShareManifestUrl({ companyId, shareToken, accessToken, versionId, buildId }) {
  const params = new URLSearchParams({ access_token: accessToken, version_id: versionId, build_id: buildId });
  return `${apiUrl}/company/${companyId}/runway/share/${shareToken}/manifest.plist?${params.toString()}`;
}

// UDID (Ad-Hoc distribution device lookup) - src/pages/Udid.js. Mints a one-off, single-use token
// tied to the signed-in caller (see create-request.js); the resulting mobileconfig URL is then
// opened directly on the device itself (Safari, not this app's own JS), which is why it's a plain
// URL builder rather than an axios call, same as getRunwayShareManifestUrl above.
export async function createUdidRequest({ userIdToken, companyId }) {
  const url = `${apiUrl}/company/${companyId}/udid/request`;
  const res = await axios.post(
    url,
    {},
    {
      headers: {
        Authorization: `Bearer ${userIdToken}`,
      },
    }
  );
  return res.data;
}

export function getUdidProfileUrl({ companyId, token }) {
  return `${apiUrl}/company/${companyId}/udid/profile/${token}`;
}
